import { Box, HStack, IconButton, ModalCloseButton, ModalHeader, Text } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { MaterialIcon } from '../../../ui/MaterialIcon';

interface CreateModalHeaderProps {
  readonly title: string;
  /** 1 or 2; absent while picking a destination. */
  readonly step?: 1 | 2;
  /** Absent: nowhere to go back to, or a request is in flight. */
  readonly onBack?: () => void;
  /** Hide the close button while a request is in flight. */
  readonly canClose: boolean;
}

/**
 * Back, title, progress, close. Only the title is the `ModalHeader` (so it
 * alone names the dialog), and it takes focus whenever it changes: the control
 * that changed the step has just unmounted, and without this focus fell to
 * the page and Escape stopped reaching the modal.
 */
export function CreateModalHeader({ title, step, onBack, canClose }: CreateModalHeaderProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [title]);
  return (
    <HStack spacing={2} pl={{ base: 4, md: 6 }} pr={14} py={4} position="relative">
      {onBack && (
        <IconButton
          aria-label="Back"
          variant="ghost"
          size="sm"
          icon={<MaterialIcon name="arrow_back" boxSize={5} />}
          onClick={onBack}
        />
      )}
      <ModalHeader
        ref={titleRef}
        as="h2"
        tabIndex={-1}
        p={0}
        flex={1}
        fontSize="lg"
        fontWeight="semibold"
        color="text.primary"
        noOfLines={1}
        // The theme's header rule would underline the title alone, not the row.
        borderBottom="none"
        // Focused by script so Escape keeps reaching the modal, never by Tab
        // (tabIndex -1): the global focus-visible ring would box the title.
        _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      >
        {title}
      </ModalHeader>
      {step && (
        <HStack spacing={1} aria-label={`Step ${step} of 2`} role="img" flexShrink={0}>
          {[1, 2].map((n) => (
            <Box
              key={n}
              w="22px"
              h="4px"
              borderRadius="full"
              bg={n <= step ? 'border.focus' : 'border.default'}
            />
          ))}
          <Text fontSize="xs" color="text.secondary" ml={1} display={{ base: 'none', md: 'block' }}>
            Step {step} of 2
          </Text>
        </HStack>
      )}
      {canClose && <ModalCloseButton top="50%" transform="translateY(-50%)" right={3} />}
    </HStack>
  );
}
